<template>
    <div class="package-card">
        <div class="step-heading">Let's understand your needs and goals — and get you the most out of your salary schedule.</div>
        <div class="step-sub">Where you teach and where you sit on your district's salary schedule determine how many clock hours will actually move you. Confirm what we have so your bundle fits.</div>

        <div class="has-text-grey mt-5" v-if="loadingProfile"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading your information...</div>
        <div v-else>
            <div class="columns mt-4 mb-0">
                <div class="column">
                    <b-field label="District" label-position="inside">
                        <b-select v-model="district_id" expanded>
                            <option :value="0">Not Listed/Not Applicable</option>
                            <option :value="999999">Private School</option>
                            <option v-for="district in districts" :key="'district-' + district.id" :value="district.id">{{ district.name }} ({{ district.city | capitalize }})</option>
                        </b-select>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="School" label-position="inside">
                        <b-select v-model="school_id" expanded>
                            <option :value="0">Not Listed/Not Applicable</option>
                            <option v-for="school in schools" :key="'school-' + school.id" :value="school.id">{{ school.name }} ({{ school.city | capitalize }})</option>
                        </b-select>
                    </b-field>
                </div>
            </div>
            <div class="columns mb-0">
                <div class="column">
                    <b-field label="Highest degree level" label-position="inside">
                        <b-select v-model="meta.highest_degree" expanded placeholder="Select...">
                            <option v-for="option in degreeOptions" :key="'degree-' + option" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Current clock hour/credit column" label-position="inside">
                        <b-select v-model="meta.hours_column" expanded placeholder="Select...">
                            <!-- The district's actual columns when a schedule is
                                 on file; the standard ladder otherwise -->
                            <template v-if="scaleColumns">
                                <option v-for="option in scaleColumns" :key="'column-' + option.value" :value="option.value">{{ option.label }}</option>
                                <option value="This does not apply">This does not apply</option>
                            </template>
                            <template v-else>
                                <option v-for="option in hoursOptions" :key="'column-' + option" :value="option">{{ option }}</option>
                            </template>
                        </b-select>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Years of experience" label-position="inside">
                        <b-select v-model="meta.years_teaching" expanded placeholder="Select...">
                            <option v-for="option in yearsOptions" :key="'years-' + option" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>
                </div>
            </div>

            <!-- Certificate renewal (optional) — feeds the dashboard's
                 professional-development tracking; saved with the profile -->
            <pd-renewal-fields v-model="meta" class="mt-2" intro="Optional: when does your certificate renew, and how many clock hours do you already have on file? Your dashboard will track your progress."/>

        </div>

        <hr/>

        <div class="step-heading">How many clock hours do you need?</div>
        <div class="step-sub" v-if="bumpTargets.length">Pick the column move you're after — each bundle is sized to get you all the way there — or build a custom amount.</div>
        <div class="step-sub" v-else>Choose any amount in 10-hour steps — bundles start at {{ hoursMin }} clock hours. Buy your hours once, at a bundle price, then spend them across any courses in the catalog.</div>

        <!-- Bump-up packages: one per reachable salary column, sized to the
             hours that complete the move -->
        <div class="columns is-multiline mt-4" v-if="bumpTargets.length">
            <div class="column is-one-third-widescreen is-half-tablet" v-for="target in bumpTargets" :key="'bump-' + target.tier">
                <div class="tier-card" :class="{'is-selected': pkgHours === packageHoursFor(target)}" @click="setPkgHours(packageHoursFor(target))">
                    <div class="tier-hours">Move to {{ target.tier }}</div>
                    <div class="tier-sub">{{ packageHoursFor(target) }} clock hours</div>
                    <div class="badge-row"><span class="badge">+{{ fmtMoney(target.diff) }} / yr on your pay</span></div>
                    <div class="tier-price">{{ fmtMoney(upfrontFor(packageHoursFor(target))) }}<span class="price-label"> bundle cost</span></div>
                    <div class="tier-deferred">+ {{ fmtMoney(deferredFor(packageHoursFor(target))) }} in clock hour fees, as you complete</div>
                    <div class="tier-alacarte">{{ fmtMoney(alacarteFor(packageHoursFor(target))) }} à la carte</div>
                    <div class="tier-save">Save {{ fmtMoney(alacarteFor(packageHoursFor(target)) - priceFor(packageHoursFor(target))) }} ({{ Math.round((1 - priceFor(packageHoursFor(target)) / alacarteFor(packageHoursFor(target))) * 100) }}%)</div>
                </div>
            </div>
        </div>

        <div class="hours-picker" :class="{'is-selected': customSelected}">
            <div class="picker-control">
                <div class="picker-label">{{ bumpTargets.length ? 'Or choose a custom amount' : 'Clock hours' }}</div>
                <b-numberinput :value="pkgHours" @input="setPkgHours" :min="0" :max="hoursMax" :step="hoursStep" controls-position="compact" :editable="false"/>
            </div>
            <div class="picker-pricing" v-if="pkgHours">
                <div class="price">{{ fmtMoney(upfrontFor(pkgHours)) }}<span class="price-label"> bundle cost</span></div>
                <div class="deferred-line">+ {{ fmtMoney(deferredFor(pkgHours)) }} in clock hour fees, as you complete</div>
                <div class="alacarte">{{ fmtMoney(alacarteFor(pkgHours)) }} à la carte</div>
                <div class="save-pill">Save {{ fmtMoney(alacarteFor(pkgHours) - priceFor(pkgHours)) }} ({{ Math.round((1 - priceFor(pkgHours) / alacarteFor(pkgHours)) * 100) }}%)</div>
            </div>
            <div class="picker-pricing empty" v-else>
                Add hours to see your bundle price and savings.
            </div>
        </div>

        <!-- The legacy leads-tool pitch (where you are / where you could be /
             what waiting costs), charting whichever option is selected above -->
        <package-pitch
            v-if="!loadingProfile"
            class="mt-5"
            :scale="scale"
            :scale-loading="scaleLoading"
            :has-district="hasRealDistrict"
            :district-id="hasRealDistrict ? district_id : null"
            :district-name="profile.district || ''"
            :position="position"
            :target="chartTarget"
            :pkg-hours="pkgHours"/>

        <div class="step-actions">
            <b-button type="is-text" @click="$emit('back')"><i class="fas fa-arrow-left mr-2"></i>Back</b-button>
            <b-button type="is-primary" size="is-medium" :disabled="!pkgHours || saving" @click="next">
                <i class="fas fa-spin fa-spinner mr-2" v-if="saving"></i>Continue: Your Focus<i class="fas fa-arrow-right ml-2"></i>
            </b-button>
        </div>
    </div>
</template>

<script>
    import {mapGetters, mapMutations} from "vuex";
    import {client as http} from "@/http_client";
    import {DEGREE_OPTIONS, HOURS_COLUMN_OPTIONS, YEARS_TEACHING_OPTIONS} from "@/constants/profile";
    import {HOURS_MIN, HOURS_MAX, HOURS_STEP, priceForHours, upfrontForHours, deferredForHours, alacarteForHours} from "@/constants/packages";
    import {computePosition, scaleColumnOptions} from "../scaleMath";
    import PackagePitch from "../PackagePitch";
    import PdRenewalFields from "@/components/fields/PdRenewalFields";

    export default {
        name: "StepNeeds",
        components: {PackagePitch, PdRenewalFields},
        data: function() {
            return {
                loadingProfile: true,
                saving: false,
                hoursMin: HOURS_MIN,
                hoursMax: HOURS_MAX,
                hoursStep: HOURS_STEP,
                degreeOptions: DEGREE_OPTIONS,
                hoursOptions: HOURS_COLUMN_OPTIONS,
                yearsOptions: YEARS_TEACHING_OPTIONS,
                scale: null,
                scaleLoading: false,
                districts: [],
                schools: [],
                district_id: undefined,
                school_id: undefined,
                initialSchoolId: null,
                profile: {},
                meta: {highest_degree: null, hours_column: null, years_teaching: null, pd_renewal_on: null, pd_external_hours: null, pd_target_hours: null},
                saved: null,
                initializing: true
            };
        },
        computed: {
            ...mapGetters(['pkgHours']),
            hasRealDistrict() {
                return !!this.district_id && this.district_id !== 0 && this.district_id !== 999999;
            },
            position() {
                return computePosition(this.scale, this.meta.highest_degree, this.meta.hours_column, this.meta.years_teaching);
            },
            scaleColumns() {
                return scaleColumnOptions(this.scale, this.meta.highest_degree);
            },
            // Only column moves a single package can cover become cards;
            // bigger jumps are reached through repeat purchases
            bumpTargets() {
                if (!this.position) return [];
                return this.position.targets.filter(t => t.requiredHours <= HOURS_MAX);
            },
            customSelected() {
                return this.pkgHours > 0 && !this.bumpTargets.some(t => t.requiredHours === this.pkgHours);
            },
            // The lane the pitch charts: the chosen bump, else the best lane a
            // custom amount reaches, else the next lane up as the aspiration
            // (chartable even when the move takes more than one package)
            chartTarget() {
                const targets = this.position ? this.position.targets : [];
                if (!targets.length) return null;
                const exact = targets.find(t => this.packageHoursFor(t) === this.pkgHours);
                if (exact) return exact;
                if (this.pkgHours > 0) {
                    const reached = [...targets].reverse().find(t => t.requiredHours <= this.pkgHours);
                    if (reached) return reached;
                }
                return targets[0];
            },
            dirty() {
                if (!this.saved) return false;
                return this.saved.district_id !== this.district_id
                    || this.saved.school_id !== this.school_id
                    || this.saved.highest_degree !== this.meta.highest_degree
                    || this.saved.hours_column !== this.meta.hours_column
                    || this.saved.years_teaching !== this.meta.years_teaching
                    || this.saved.pd_renewal_on !== this.meta.pd_renewal_on
                    || this.saved.pd_external_hours !== this.meta.pd_external_hours
                    || this.saved.pd_target_hours !== this.meta.pd_target_hours;
            }
        },
        methods: {
            ...mapMutations(['setPkgHours', 'setUser']),
            // Standard-split pricing throughout this step — the actual split
            // is chosen later, on the payment screen
            priceFor(hours) {
                return priceForHours(hours);
            },
            upfrontFor(hours) {
                return upfrontForHours(hours);
            },
            deferredFor(hours) {
                return deferredForHours(hours);
            },
            // Cards are sized to complete the move, never below the minimum package
            packageHoursFor(target) {
                return Math.max(HOURS_MIN, target.requiredHours);
            },
            alacarteFor(hours) {
                return alacarteForHours(hours);
            },
            fmtMoney(value) {
                return '$' + Math.round(value).toLocaleString('en-US');
            },
            load() {
                http.get('app/users/profile-verification', {force: true}).then(response => {
                    this.profile = {...response.data.profile};
                    this.meta = {...this.meta, ...response.data.meta};
                    this.initialSchoolId = this.profile.school_id;
                    this.loadDistricts();
                }).catch(err => {
                    console.error('Could not load profile information.', err);
                    this.loadingProfile = false;
                });
            },
            loadDistricts() {
                http.get('districts').then(results => {
                    this.districts = results.data.filter(d => d.id !== 9999999);
                    if (this.profile.district === 'Private School') {
                        this.district_id = 999999;
                    } else if (this.profile.district_id && this.districts.some(d => d.id === this.profile.district_id)) {
                        this.district_id = this.profile.district_id;
                    } else {
                        this.district_id = 0;
                    }
                    this.loadingProfile = false;
                    // No school lookup pending for the synthetic districts —
                    // the snapshot can settle as soon as the watchers run.
                    // Real districts snapshot once their school list resolves.
                    if (this.district_id === 0 || this.district_id === 999999) {
                        this.$nextTick(() => this.snapshot());
                    }
                }).catch(err => {
                    console.error(err);
                    this.loadingProfile = false;
                });
            },
            // Dirty-tracking baseline, taken after the prefill (including the
            // async school-list restore) has settled
            snapshot() {
                this.saved = {
                    district_id: this.district_id,
                    school_id: this.school_id,
                    highest_degree: this.meta.highest_degree,
                    hours_column: this.meta.hours_column,
                    years_teaching: this.meta.years_teaching,
                    pd_renewal_on: this.meta.pd_renewal_on,
                    pd_external_hours: this.meta.pd_external_hours,
                    pd_target_hours: this.meta.pd_target_hours
                };
                this.initializing = false;
            },
            loadScale(districtId) {
                this.scale = null;
                if (!districtId || districtId === 999999) {
                    this.scaleLoading = false;
                    return;
                }
                this.scaleLoading = true;
                http.get('districts/' + districtId + '/pay-scale').then(response => {
                    this.scale = response.data;
                    this.scaleLoading = false;
                }).catch(() => {
                    // 404 = no schedule on file; the pitch shows its fallback
                    this.scaleLoading = false;
                });
            },
            next() {
                if (!this.pkgHours || this.saving) return;

                // Only touch the profile when something actually changed;
                // the endpoint updates just the fields it receives.
                if (!this.dirty) {
                    this.$emit('continue');
                    return;
                }

                this.saving = true;
                http.post('app/users/profile-verification', {
                    district: this.profile.district,
                    district_id: (this.district_id === 0 || this.district_id === 999999) ? null : this.district_id,
                    school: this.profile.school,
                    school_id: (this.school_id === 0) ? null : this.school_id,
                    meta: {
                        highest_degree: this.meta.highest_degree, hours_column: this.meta.hours_column, years_teaching: this.meta.years_teaching,
                        pd_renewal_on: this.meta.pd_renewal_on, pd_external_hours: this.meta.pd_external_hours, pd_target_hours: this.meta.pd_target_hours
                    }
                }).then(() => {
                    this.saving = false;
                    this.setUser({...this.$store.getters.currentUser, district: this.profile.district, school: this.profile.school});
                    this.$emit('continue');
                }).catch(err => {
                    this.saving = false;
                    console.error(err);
                    this.$notify('error', 'Could Not Save', 'Something went wrong saving your information. You can continue and update it later under Your Account.', {duration: 8000, permanent: false});
                    this.$emit('continue');
                });
            }
        },
        watch: {
            district_id: function(val) {
                this.schools = [];
                this.loadScale(val);
                if (val === 0) {
                    this.profile.district = 'Not Listed/Not Applicable';
                } else if (val === 999999) {
                    this.profile.district = 'Private School';
                } else {
                    let code;
                    this.districts.forEach(d => {
                        if (d.id === val) {
                            this.profile.district = d.name;
                            code = d.code;
                        }
                    });
                    if (code) {
                        http.get('districts/schoolsIn/' + code).then(results => {
                            this.schools = results.data;
                            if (this.initialSchoolId && this.schools.some(s => s.id === this.initialSchoolId)) {
                                this.school_id = this.initialSchoolId;
                            }
                            this.initialSchoolId = null;
                            if (this.initializing) this.$nextTick(() => this.snapshot());
                        }).catch(err => {
                            console.error(err);
                            if (this.initializing) this.snapshot();
                        });
                    } else if (this.initializing) {
                        this.$nextTick(() => this.snapshot());
                    }
                }
                if (val === 0 || val === 999999) {
                    this.school_id = 0;
                    this.initialSchoolId = null;
                } else if (!this.initialSchoolId) {
                    this.school_id = undefined;
                }
            },
            school_id: function(val) {
                if (val === 0) {
                    this.profile.school = 'Not Listed/Not Applicable';
                } else {
                    this.schools.forEach(s => {
                        if (s.id === val) this.profile.school = s.name;
                    });
                }
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    hr {
        margin: 1.8rem 0 1.6rem;
        background-color: #eef1f5;
    }

    .tier-card {
        border: 2px solid #e1e7ee;
        border-radius: 6px;
        padding: 1.1rem 1.3rem;
        cursor: pointer;
        height: 100%;

        &:hover {
            border-color: #7fa8cd;
        }

        &.is-selected {
            border-color: #2e5f8f;
            background: #f2f7fc;
        }

        .badge-row {
            margin: 0.65rem 0 0.15rem;

            .badge {
                display: inline-block;
                background: #e7f4ec;
                color: #2e7d4f;
                font-size: 0.7rem;
                font-weight: 900;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                padding: 3px 8px;
                border-radius: 999px;
            }
        }

        .tier-hours {
            font-size: 1.05rem;
            font-weight: 900;
            color: #33475b;
            margin-top: 4px;
        }

        .tier-sub {
            font-size: 0.85rem;
            color: #666d7a;
            margin-top: 1px;
        }

        .tier-price {
            font-size: 1.6rem;
            font-weight: 900;
            color: #2e5f8f;
            margin-top: 0.5rem;

            .price-label {
                font-size: 0.85rem;
                font-weight: bold;
                color: #666d7a;
            }
        }

        .tier-deferred {
            font-size: 0.8rem;
            color: #666d7a;
            margin-top: 1px;
        }

        .tier-alacarte {
            font-size: 0.8rem;
            color: #7a8a9d;
            margin-top: 0.5rem;
            text-decoration: line-through;
        }

        .tier-save {
            display: inline-block;
            margin-top: 0.4rem;
            background: #e7f4ec;
            color: #2e7d4f;
            font-size: 0.75rem;
            font-weight: bold;
            padding: 4px 10px;
            border-radius: 999px;
        }
    }

    .hours-picker {
        display: flex;
        align-items: center;
        gap: 2.5rem;
        border: 2px solid #e1e7ee;
        border-radius: 6px;
        padding: 1.3rem 1.6rem;
        margin-top: 1rem;
        flex-wrap: wrap;

        &.is-selected {
            border-color: #2e5f8f;
            background: #f2f7fc;
        }

        .picker-control {
            .picker-label {
                font-size: 0.8rem;
                font-weight: bold;
                color: #33475b;
                margin-bottom: 0.4rem;
            }
        }

        .picker-pricing {
            flex: 1;
            min-width: 200px;

            &.empty {
                font-size: 0.9rem;
                color: #7a8a9d;
            }

            .price {
                font-size: 1.7rem;
                font-weight: 900;
                color: #2e5f8f;

                .price-label {
                    font-size: 0.85rem;
                    font-weight: bold;
                    color: #666d7a;
                }
            }

            .deferred-line {
                font-size: 0.8rem;
                color: #666d7a;
            }

            .alacarte {
                font-size: 0.85rem;
                color: #7a8a9d;
                text-decoration: line-through;
                margin-top: 0.3rem;
            }

            .save-pill {
                display: inline-block;
                margin-top: 0.4rem;
                background: #e7f4ec;
                color: #2e7d4f;
                font-size: 0.75rem;
                font-weight: bold;
                padding: 4px 10px;
                border-radius: 999px;
            }
        }
    }
</style>
